/**
 * VerificationFlowController - Manages interactive device verification flows
 *
 * Handles:
 * - QR code verification
 * - SAS (emoji/number) verification
 * - Verification request lifecycle
 * - Event handling and state management
 */

import matrixService from '../../services/matrixService';
import logger from '../../utils/logger';
import { trustEventEmitter, TrustEvents } from '../events';

// Verification states
export const VerificationState = {
  IDLE: 'idle',
  REQUESTING: 'requesting',
  READY: 'ready',
  SHOW_QR: 'show_qr',
  SCAN_QR: 'scan_qr',
  SHOW_SAS: 'show_sas',
  VERIFYING: 'verifying',
  COMPLETE: 'complete',
  CANCELLED: 'cancelled',
  ERROR: 'error',
};

// Verification methods
export const VerificationMethod = {
  QR: 'm.qr_code.show.v1',
  SAS: 'm.sas.v1',
};

const VERIFICATION_TIMEOUT = 5 * 60 * 1000; // 5 minutes

class VerificationFlowController {
  constructor() {
    this.state = VerificationState.IDLE;
    this.request = null;
    this.verifier = null;
    this.timeoutId = null;
    this.listeners = new Map();
    this.currentMethod = null;
    this.sasData = null;
    this.sasObject = null;  // Store SAS object with confirm/mismatch methods
    this.qrData = null;
    this.stateChangeHandlers = new Set();
  }

  /**
   * Start fallback verification for current user
   * @returns {Promise<'success' | 'cancel' | 'error'>} Verification result
   */
  async startFallbackForSelf() {
    // CRITICAL: Prevent starting if already in progress or recently completed
    if (this.state !== VerificationState.IDLE) {
      logger.warn('VerificationFlowController: Verification already in progress or not idle, state:', this.state);
      return 'error';
    }

    const client = matrixService.client;
    if (!client) {
      logger.error('VerificationFlowController: No Matrix client available');
      return 'error';
    }

    const userId = client.getUserId();
    if (!userId) {
      logger.error('VerificationFlowController: No user ID available');
      return 'error';
    }

    try {
      logger.info('Starting self-verification flow');
      trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_START, { userId });
      this.setState(VerificationState.REQUESTING);

      // Start verification request
      const crypto = client.getCrypto();
      if (!crypto) {
        throw new Error('Crypto module not available');
      }
      
      if (!crypto.requestOwnUserVerification) {
        throw new Error('Verification not supported by crypto module - requestOwnUserVerification not available');
      }

      logger.debug('VerificationFlowController: Requesting verification for self using requestOwnUserVerification');
      this.request = await crypto.requestOwnUserVerification();

      // Manually trigger initial state check
      if (this.request) {
        setTimeout(() => {
          this._handleRequestChange();
        }, 100);
      }

      // Set up timeout
      this.timeoutId = setTimeout(() => {
        logger.warn('Verification request timed out');
        this.cancel('timeout');
      }, VERIFICATION_TIMEOUT);

      // Listen for verification events
      this._setupRequestListeners();

      // Wait for verification to complete
      return await this._waitForCompletion();
    } catch (error) {
      logger.error('VerificationFlowController: Failed to start verification:', error);
      this.setState(VerificationState.ERROR);
      trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_ERROR, {
        error: error.message,
      });
      return 'error';
    }
  }

  /**
   * Set up event listeners for verification request
   * @private
   */
  _setupRequestListeners() {
    if (!this.request) return;

    // Listen for state changes
    const changeHandler = () => {
      this._handleRequestChange();
    };
    this.request.on('change', changeHandler);
    this.listeners.set('change', changeHandler);

    // Listen for verifier creation
    if (this.request.verifier) {
      this._setupVerifierListeners(this.request.verifier);
    } else {
      const verifierHandler = (verifier) => {
        this.verifier = verifier;
        this._setupVerifierListeners(verifier);
      };
      this.request.once('verifier', verifierHandler);
      this.listeners.set('verifier', verifierHandler);
    }
  }

  /**
   * Set up event listeners for verifier
   * @private
   */
  _setupVerifierListeners(verifier) {
    if (!verifier) return;

    this.verifier = verifier;
    logger.debug('VerificationFlowController: Verifier created');

    // Determine verification method
    // Prefer SAS (emoji) for device verification as it's more user-friendly
    // QR codes are better for cross-signing but require camera access
    if (verifier.getShowSasCallbacks) {
      this.currentMethod = VerificationMethod.SAS;
      this._setupSASFlow(verifier);
    } else if (verifier.getReciprocateQrCodeCallbacks) {
      this.currentMethod = VerificationMethod.QR;
      this._setupQRFlow(verifier);
    }

    // Listen for completion
    verifier.on('done', () => {
      logger.debug('VerificationFlowController: Verification complete');
      this.setState(VerificationState.COMPLETE);
    });

    // Listen for cancellation
    verifier.on('cancel', (reason) => {
      logger.debug('VerificationFlowController: Verification cancelled:', reason);
      this.setState(VerificationState.CANCELLED);
    });
  }

  /**
   * Set up QR code verification flow
   * @private
   */
  _setupQRFlow(verifier) {
    const callbacks = verifier.getReciprocateQrCodeCallbacks();

    if (callbacks?.generateQrCode) {
      // We show QR code
      callbacks.generateQrCode().then(qrData => {
        logger.debug('VerificationFlowController: QR code generated');
        this.qrData = qrData;
        this.setState(VerificationState.SHOW_QR);
      }).catch(error => {
        logger.error('VerificationFlowController: Failed to generate QR:', error);
        this.setState(VerificationState.ERROR);
      });
    } else if (callbacks?.scanQrCode) {
      // We scan QR code
      this.setState(VerificationState.SCAN_QR);
    }
  }

  /**
   * Set up SAS (emoji/number) verification flow
   * @private
   */
  _setupSASFlow(verifier) {
    // Matrix SDK Rust crypto uses events, not callbacks
    // Listen for the show_sas event which fires when emojis are ready
    const showSasHandler = (sas) => {
      logger.info('SAS emojis ready for comparison');

      // CRITICAL: Store the entire SAS object - it has confirm/mismatch/cancel methods
      this.sasObject = sas;

      // Rust crypto emits callbacks object with nested sas property
      // The structure is: { sas: { emoji: [...], decimal: [...] }, confirm, mismatch, cancel }
      this.sasData = {
        emoji: sas?.sas?.emoji || sas?.emoji || null,
        decimal: sas?.sas?.decimal || sas?.decimal || null,
      };
      
      this.setState(VerificationState.SHOW_SAS);
    };

    // Register the event listener
    verifier.on('show_sas', showSasHandler);
    
    // Now call verify() to start the SAS exchange
    // This triggers emoji generation on both sides
    if (verifier.verify) {
      try {
        verifier.verify().catch((err) => {
          logger.error('VerificationFlowController: verify() failed:', err);
        });
      } catch (err) {
        logger.error('VerificationFlowController: Failed to call verify():', err);
      }
    }
  }

  /**
   * Handle verification request state changes
   * @private
   */
  _handleRequestChange() {
    if (!this.request) {
      return;
    }

    const phase = this.request.phase;
    logger.debug('VerificationFlowController: Request phase:', phase);

    // Matrix SDK Rust crypto phase values:
    // Phase 0 = Unsent
    // Phase 1 = Initial/Created
    // Phase 2 = Requested (sent, waiting for other device to respond)
    // Phase 3 = Ready (other device accepted, ready to start verification)
    // Phase 4 = Started (verification in progress)
    // Phase 5 = Cancelled
    // Phase 6 = Done

    // Handle numeric phases (Rust crypto)
    if (typeof phase === 'number') {
      switch (phase) {
        case 0: // Unsent
        case 1: // Initial/Created
        case 2: // Requested - sent to other devices, waiting for response
          // Stay in REQUESTING state
          break;
        case 3: // Ready - other device has accepted
          logger.info('Verification request accepted by other device');
          this.setState(VerificationState.READY);
          
          // Check if verifier was created
          if (this.request?.verifier && !this.verifier) {
            this._setupVerifierListeners(this.request.verifier);
          } else if (!this.request?.verifier) {
            // No verifier yet - as the INITIATOR, we should start verification
            // Element Web shows a button, but for device verification we can auto-start
            (async () => {
              try {
                const verifier = await this.request.startVerification('m.sas.v1');
                if (verifier && !this.verifier) {
                  this._setupVerifierListeners(verifier);
                }
              } catch (err) {
                logger.error('VerificationFlowController: Failed to start verification:', err);
              }
            })();
          }
          break;
        case 4: // Started - verification in progress
          // Verifier should be created at this point
          break;
        case 5: // Cancelled
          logger.warn('Verification cancelled by other device or timed out');
          this.setState(VerificationState.CANCELLED);
          break;
        case 6: // Done
          this.setState(VerificationState.COMPLETE);
          break;
      }
    } else {
      // Handle string phases (legacy)
      switch (phase) {
        case 'ready':
          this.setState(VerificationState.READY);
          break;
        case 'done':
          this.setState(VerificationState.COMPLETE);
          break;
        case 'cancelled':
          this.setState(VerificationState.CANCELLED);
          break;
      }
    }
  }

  /**
   * Confirm SAS match (user action)
   */
  async confirmSasMatch() {
    if (this.state !== VerificationState.SHOW_SAS || !this.sasObject) {
      logger.error('VerificationFlowController: Cannot confirm SAS in current state');
      return false;
    }

    try {
      logger.info('User confirmed SAS match');
      this.setState(VerificationState.VERIFYING);
      // Use the SAS object's confirm method, not verifier.confirm()
      await this.sasObject.confirm();
      return true;
    } catch (error) {
      logger.error('VerificationFlowController: Failed to confirm SAS:', error);
      this.setState(VerificationState.ERROR);
      return false;
    }
  }

  /**
   * Scan QR code (user action)
   * @param {string} qrData - QR code data from scanner
   */
  async scanQrCode(qrData) {
    if (this.state !== VerificationState.SCAN_QR || !this.verifier) {
      logger.error('VerificationFlowController: Cannot scan QR in current state');
      return false;
    }

    try {
      this.setState(VerificationState.VERIFYING);
      const callbacks = this.verifier.getReciprocateQrCodeCallbacks();
      if (callbacks?.scanQrCode) {
        await callbacks.scanQrCode(qrData);
      }
      return true;
    } catch (error) {
      logger.error('VerificationFlowController: Failed to scan QR:', error);
      this.setState(VerificationState.ERROR);
      return false;
    }
  }

  /**
   * Cancel verification
   * @param {string} reason - Cancellation reason
   */
  cancel(reason = 'user') {
    logger.warn(`Verification cancelled: ${reason}`);

    if (this.verifier) {
      try {
        this.verifier.cancel(reason);
      } catch (error) {
        logger.warn('VerificationFlowController: Error cancelling verifier:', error);
      }
    }

    if (this.request) {
      try {
        this.request.cancel(reason);
      } catch (error) {
        logger.warn('VerificationFlowController: Error cancelling request:', error);
      }
    }

    this.setState(VerificationState.CANCELLED);
    // Preserve CANCELLED state for observers; do not reset to IDLE here
    this._cleanup({ resetState: false });
  }

  /**
   * Wait for verification to complete
   * @private
   * @returns {Promise<'success' | 'cancel' | 'error'>}
   */
  async _waitForCompletion() {
    return new Promise((resolve) => {
      const checkState = () => {
        switch (this.state) {
          case VerificationState.COMPLETE:
            logger.info('Verification completed successfully');
            trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_COMPLETE, {});
            // Don't automatically reset state to IDLE - let the modal handle cleanup
            // This prevents the verification loop where modal remounts and sees IDLE state
            // The modal will call reset() when user clicks OK
            setTimeout(() => {
              this._cleanup({ resetState: false });
            }, 1000);
            resolve('success');
            break;
          case VerificationState.CANCELLED:
            trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_CANCEL, {});
            this._cleanup({ resetState: true });
            resolve('cancel');
            break;
          case VerificationState.ERROR:
            trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_ERROR, {});
            this._cleanup({ resetState: true });
            resolve('error');
            break;
        }
      };

      // Listen for state changes
      const stateHandler = () => checkState();
      this.stateChangeHandlers.add(stateHandler);

      // Check initial state
      checkState();
    });
  }

  /**
   * Set controller state
   * @private
   */
  setState(newState) {
    if (this.state === newState) return;

    logger.debug(`VerificationFlowController: State change ${this.state} -> ${newState}`);
    this.state = newState;

    // Notify state change handlers
    this.stateChangeHandlers.forEach(handler => {
      try {
        handler(newState);
      } catch (error) {
        logger.error('VerificationFlowController: State handler error:', error);
      }
    });
  }

  /**
   * Subscribe to state changes
   * @param {Function} handler - State change handler
   * @returns {Function} Unsubscribe function
   */
  onStateChange(handler) {
    this.stateChangeHandlers.add(handler);
    return () => {
      this.stateChangeHandlers.delete(handler);
    };
  }

  /**
   * Get current state and data
   */
  getCurrentState() {
    return {
      state: this.state,
      method: this.currentMethod,
      sasData: this.sasData,
      qrData: this.qrData,
    };
  }

  /**
   * Clean up resources
   * @private
   */
  _cleanup({ resetState = true } = {}) {
    // Clear timeout
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    // Remove listeners
    if (this.request) {
      this.listeners.forEach((handler, event) => {
        this.request.off(event, handler);
      });
    }
    this.listeners.clear();

    // Clear state
    this.request = null;
    this.verifier = null;
    this.sasData = null;
    this.sasObject = null;
    this.qrData = null;
    this.currentMethod = null;
    if (resetState) {
      this.state = VerificationState.IDLE;
    }
    this.stateChangeHandlers.clear();
  }

  /**
   * Accept incoming verification request
   * Called when user accepts a verification request from another device
   * @param {Object} request - Matrix verification request object
   * @returns {Promise<'success' | 'cancel' | 'error'>} Verification result
   */
  async acceptIncomingRequest(request) {
    if (this.state !== VerificationState.IDLE) {
      logger.warn('VerificationFlowController: Cannot accept request in current state:', this.state);
      return 'error';
    }

    if (!request) {
      logger.error('VerificationFlowController: No request provided');
      return 'error';
    }

    try {
      this.request = request;
      this.setState(VerificationState.REQUESTING);

      logger.debug('VerificationFlowController: Accepting incoming verification request');
      
      // Accept the request
      await request.accept();

      // Set up timeout
      this.timeoutId = setTimeout(() => {
        logger.warn('VerificationFlowController: Verification timeout');
        this.cancel('timeout');
      }, VERIFICATION_TIMEOUT);

      // Listen for verification events
      this._setupRequestListeners();

      // Wait for verification to complete
      return await this._waitForCompletion();
    } catch (error) {
      logger.error('VerificationFlowController: Failed to accept request:', error);
      this.setState(VerificationState.ERROR);
      trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_ERROR, {
        error: error.message,
      });
      return 'error';
    }
  }

  /**
   * Reject incoming verification request
   * @param {Object} request - Matrix verification request object
   * @param {string} reason - Reason for rejection (default: 'user')
   * @returns {Promise<boolean>} True if successfully rejected
   */
  async rejectIncomingRequest(request, reason = 'user') {
    if (!request) {
      logger.error('VerificationFlowController: No request provided');
      return false;
    }

    try {
      logger.debug('VerificationFlowController: Rejecting incoming verification request');
      
      await request.cancel(reason);
      
      trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_CANCEL, {
        reason,
      });

      return true;
    } catch (error) {
      logger.error('VerificationFlowController: Failed to reject request:', error);
      return false;
    }
  }

  /**
   * Reset controller (for testing or re-use)
   * NOTE: This sends cancellation to remote device - use resetLocalState() if verification already ended
   */
  reset() {
    this.cancel('reset');
    this._cleanup({ resetState: true });
  }

  /**
   * Reset local state only (without sending remote cancellation)
   * Use this when modal closes after verification is already cancelled/complete
   * This allows fresh retry without interfering with remote device state
   *
   * Protects against stale cleanups from a closing modal clobbering
   * a new verification that started on this singleton controller.
   */
  resetLocalState() {
    // Don't reset if a new verification is actively in progress
    const activeStates = [
      VerificationState.REQUESTING, VerificationState.READY,
      VerificationState.SHOW_SAS, VerificationState.SHOW_QR,
      VerificationState.SCAN_QR, VerificationState.VERIFYING,
    ];
    if (activeStates.includes(this.state)) {
      logger.debug('VerificationFlowController: Skipping reset - verification in progress, state:', this.state);
      return;
    }
    this._cleanup({ resetState: true });
  }
}

// Create singleton instance
const verificationFlowController = new VerificationFlowController();
export default verificationFlowController;

// Also export class for testing
export { VerificationFlowController };